import { Grid, Box } from '@mui/material';
import { FunctionComponent, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import useAppDispatch from '../../hooks/useAppDispatch';
import { Navbar, Loader } from 'src/shared';
import ProjectContributors from './components/ProjectContributors';
import TaskQueue from './components/TaskQueue';
import ProjectActivity from './components/ProjectActivity';
import ProjectDetails from './components/ProjectDetail';
import { getIsLoading } from '../../redux/project-contributors/selectors';
import { ProjectActivityCard } from './styled';
import BudgetReview from './components/BudgetReview';
import {
    getActivityStat,
    getProjectData,
    getProjectDetails
} from 'src/redux/projects/service';
import { getProjectDataFromState } from 'src/redux/projects/selectors';
import { useNavigate } from 'react-router-dom';
import { signedIn } from 'src/redux/auth/service';
import { IActivityRange } from 'src/constants/activityStat';
import DelayedTask from 'src/shared/DelayedTask';
import { ClientSelectors } from 'src/redux/client/selectors';

type Props = Record<string, never>;

const ProjectDashboard: FunctionComponent<Props> = ({}: Props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { selectedProjectId } = ClientSelectors();
    const projectData = useSelector((state) =>
        selectedProjectId
            ? getProjectDataFromState(selectedProjectId)(state)
            : null
    );
    const isLoading = useSelector(getIsLoading);
    useEffect(() => {
        if (!signedIn()) {
            navigate('/login');
        }
    }, [navigate]);

    const lazyFetchProjectData = useCallback(async () => {
        if (selectedProjectId) {
            await dispatch(getProjectData({ id: selectedProjectId }));
            dispatch(getProjectDetails(selectedProjectId));
        }
    }, [dispatch, selectedProjectId]);

    useEffect(() => {
        dispatch(lazyFetchProjectData);
    }, [dispatch, lazyFetchProjectData]);

    const fetchActivityStat = useCallback(
        async (params: IActivityRange) => {
            await dispatch(getActivityStat(params));
        },
        [dispatch]
    );

    useEffect(() => {
        fetchActivityStat({
            from: null,
            to: null
        });
    }, [dispatch, fetchActivityStat]);

    const handleSort = (_sortBy: string) => {};

    return (
        <Navbar>
            <Box
                sx={{
                    marginLeft: {
                        xs: '10px',
                        sm: '10px',
                        md: '100px',
                        lg: '100px'
                    },
                    marginRight: {
                        xs: '10px',
                        sm: '10px',
                        md: '100px',
                        lg: '100px'
                    }
                }}
            >
                <ProjectDetails />
                <Grid
                    container
                    spacing={2}
                    columns={{ xs: 12, sm: 12, md: 10, lg: 10 }}
                >
                    <Grid item xs={12} sm={6} md={2} lg={2}>
                        <TaskQueue projectData={projectData} />
                    </Grid>
                    <Grid item xs={12} sm={12} md={8} lg={8}>
                        <ProjectActivityCard>
                            <ProjectActivity projectData={projectData} />
                        </ProjectActivityCard>
                    </Grid>
                </Grid>

                <BudgetReview projectData={projectData} />

                {isLoading ? (
                    <Loader />
                ) : (
                    <DelayedTask projectData={projectData} />
                )}
                {isLoading ? (
                    <Loader />
                ) : (
                    <ProjectContributors
                        projectData={projectData}
                        handleSort={handleSort}
                        sort="desc"
                        sortBy="name"
                    />
                )}
            </Box>
        </Navbar>
    );
};

export default ProjectDashboard;
